import React from 'react';
import { productsData } from '../data/products';

const ProductPlaceholder = ({ index }) => {
  // 为不同的占位图设置不同的渐变色
  const gradients = [
    'from-blue-400 to-indigo-500',
    'from-purple-400 to-pink-500',
    'from-green-400 to-teal-500'
  ];

  const gradient = gradients[index % gradients.length];
  const label = `开发中 ${index + 1}`;

  return (
    <div className="aspect-[9/16] overflow-hidden rounded-lg shadow-sm relative">
      <div className={`w-full h-full bg-gradient-to-br ${gradient} flex flex-col items-center justify-center p-4`}>
        <svg className="w-12 h-12 text-white/80 mb-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
        </svg>
        <span className="text-white font-medium text-center text-sm">{label}</span>
        <span className="text-white/70 text-xs mt-1">即将上线</span>
      </div>
    </div>
  );
};

const LogoPlaceholder = ({ name }) => {
  // 从产品名称生成唯一颜色
  const getColorFromName = (name) => {
    const colors = [
      'from-blue-500 to-indigo-600',
      'from-purple-500 to-pink-600',
      'from-green-500 to-teal-600',
      'from-yellow-500 to-amber-600',
      'from-red-500 to-rose-600'
    ];
    
    // 简单的哈希函数，根据产品名称选择颜色
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  const gradient = getColorFromName(name);
  const initials = name.split(' ').map(word => word[0]).join('').toUpperCase();
  
  return (
    <div className="w-24 h-24 rounded-[20px] overflow-hidden shadow-lg">
      <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
        <span className="text-white text-2xl font-bold">{initials}</span>
      </div>
    </div>
  );
};

const Product = ({ id, name, description, features, images, logo, url }) => {
  // 检查是否有真实图片
  const hasRealImages = images && images.length > 0 && !images[0].includes('placeholder');

  return (
    <a 
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-xl shadow-lg overflow-hidden transition-transform hover:scale-105 duration-300 cursor-pointer">
      <div className="p-6">
        <div className="mb-4">
          {logo ? (
            <div className="w-24 h-24 rounded-[20px] overflow-hidden shadow-lg bg-gradient-to-br from-gray-50 to-gray-100 p-1">
              <img 
                src={logo} 
                alt={`${name} logo`} 
                className="w-full h-full object-cover rounded-[16px]"
              />
            </div>
          ) : (
            <LogoPlaceholder name={name} />
          )}
        </div>
        
        <h3 className="text-2xl font-bold text-dark mb-2">{name}</h3>
        <p className="text-gray-600 mb-4">{description}</p>
        
        <h4 className="font-semibold text-dark mb-2">核心特点：</h4>
        <ul className="mb-6 space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg className="w-5 h-5 text-primary mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="px-6 pb-6">
        <div className="grid grid-cols-3 gap-4">
          {hasRealImages ? (
            // 显示真实图片
            images.map((image, index) => (
              <div key={index} className="aspect-[9/16] bg-gray-100 overflow-hidden rounded-lg shadow-sm">
                <img 
                  src={image} 
                  alt={`${name} screenshot ${index + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
            ))
          ) : (
            // 显示占位图
            [0, 1, 2].map((index) => (
              <ProductPlaceholder key={index} index={index} />
            ))
          )}
        </div>
      </div>
    </a>
  );
};

const ProductsSection = () => {

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">我们的产品</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            引力结构致力于开发简单易用、功能强大的AI应用，让科技真正走入生活
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {productsData.map((product, index) => (
            <Product
              key={index}
              id={product.id}
              name={product.name}
              description={product.description}
              features={product.features}
              images={product.images}
              logo={product.logo}
              url={product.url}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection; 